import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import api, { fmtUsd } from "../lib/api";
import { ArrowsClockwise, Ranking, CaretUp, CaretDown } from "@phosphor-icons/react";

const PERIODS = [
  { id: "24h", label: "24H" },
  { id: "7d",  label: "7D" },
  { id: "30d", label: "30D" },
  { id: "all", label: "All-time" },
];

const MEDAL = {
  1: { bg: "#FCD535", fg: "#0A0E17" },
  2: { bg: "#C7CCD6", fg: "#0A0E17" },
  3: { bg: "#E08A3C", fg: "#0A0E17" },
};

// Clean, vibrant palette that reads well with white text (no muddy olive/yellow)
const AVATAR_COLORS = [
  "#E84142", "#2EBD85", "#7B61FF", "#2F80ED", "#F2994A",
  "#EB5757", "#9B51E0", "#1FA971", "#2D9CDB", "#BB6BD9",
  "#3A86FF", "#06A77D",
];

function avatarColor(seed) {
  let h = 0;
  const s = seed || "?";
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[h];
}

function colorForPnl(v) {
  if (v > 0) return "#0ECB81";
  if (v < 0) return "#F6465D";
  return "#9CA3AF";
}

// Single element: avatar with the rank number as a corner badge (medal-colored for top 3)
function RankAvatar({ rank, name, ident, size = "md" }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  const medal = MEDAL[rank];
  const dim = size === "lg" ? "w-11 h-11" : "w-9 h-9 sm:w-10 sm:h-10";
  return (
    <div className={`relative ${dim} shrink-0`}>
      <div
        className="w-full h-full rounded-md flex items-center justify-center text-white font-bold text-base"
        style={{ backgroundColor: avatarColor((name || "") + (ident || "")) }}
      >
        {initial}
      </div>
      <div
        className="absolute -bottom-1.5 -left-1.5 min-w-[18px] h-[18px] px-1 rounded-md flex items-center justify-center mono text-[10px] font-bold border-2 border-[#0A0E17]"
        style={medal ? { backgroundColor: medal.bg, color: medal.fg } : { backgroundColor: "#1F2937", color: "#AEB4BE" }}
      >
        {rank}
      </div>
    </div>
  );
}

function Row({ r, isMe }) {
  const pnl = r.pnl || 0;
  const roi = r.roi_pct || 0;
  const c = colorForPnl(pnl);
  return (
    <div
      data-testid={`lb-row-${r.rank}`}
      className={`grid grid-cols-[1fr_110px_130px_80px_80px] items-center gap-3 px-5 py-3 border-b border-[#11161F] hover:bg-[#0E1422] transition-colors ${isMe ? "bg-[#FCD535]/[0.04]" : ""}`}
    >
      {/* Trader (rank + avatar merged) */}
      <div className="flex items-center gap-3 min-w-0">
        <RankAvatar rank={r.rank} name={r.name} ident={r.ident} />
        <div className="min-w-0">
          <div className="text-[15px] font-medium text-white truncate">
            {r.name || "—"}
            {isMe && <span className="ml-2 text-[9px] uppercase tracking-widest text-[#FCD535] font-semibold">You</span>}
          </div>
          <div className="mono text-[11px] text-[#6B7280] truncate">{r.ident}</div>
        </div>
      </div>

      {/* ROI % */}
      <div className="text-right mono text-sm font-semibold" style={{ color: colorForPnl(roi) }}>
        {roi > 0 ? "+" : ""}{roi.toFixed(2)}%
      </div>

      {/* PnL */}
      <div className="text-right">
        <div className="flex items-center justify-end gap-1 mono text-base font-semibold" style={{ color: c }}>
          {pnl > 0 && <CaretUp size={11} weight="fill" />}
          {pnl < 0 && <CaretDown size={11} weight="fill" />}
          <span>{pnl >= 0 ? "+" : ""}${fmtUsd(Math.abs(pnl))}</span>
        </div>
        <div className="mono text-[10px] text-[#6B7280] text-right mt-0.5">Equity ${fmtUsd(r.balance)}</div>
      </div>

      {/* Trades */}
      <div className="text-right mono text-sm text-[#D1D5DB]">{r.trades || 0}</div>

      {/* Win Rate */}
      <div className="text-right mono text-sm" style={{ color: r.win_rate >= 50 ? "#0ECB81" : "#9CA3AF" }}>
        {r.trades > 0 ? `${(r.win_rate || 0).toFixed(0)}%` : "—"}
      </div>
    </div>
  );
}

function MobileCard({ r, isMe }) {
  const pnl = r.pnl || 0;
  const roi = r.roi_pct || 0;
  const c = colorForPnl(pnl);
  return (
    <div
      data-testid={`lb-mobile-${r.rank}`}
      className={`bg-[#0D1117] border border-[#1F2937] p-3 ${isMe ? "border-[#FCD535]/40 bg-[#FCD535]/[0.04]" : ""}`}
    >
      <div className="flex items-center gap-3">
        <RankAvatar rank={r.rank} name={r.name} ident={r.ident} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white truncate">
            {r.name || "—"}
            {isMe && <span className="ml-1.5 text-[9px] uppercase tracking-widest text-[#FCD535] font-semibold">You</span>}
          </div>
          <div className="mono text-[10px] text-[#6B7280] truncate">{r.ident}</div>
        </div>
        <div className="text-right shrink-0 pl-1">
          <div className="mono text-sm font-semibold whitespace-nowrap" style={{ color: c }}>
            {pnl >= 0 ? "+" : ""}${fmtUsd(Math.abs(pnl))}
          </div>
          <div className="mono text-[10px] whitespace-nowrap" style={{ color: colorForPnl(roi) }}>
            {roi > 0 ? "+" : ""}{roi.toFixed(2)}%
          </div>
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-[#1F2937] grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#6B7280]">Equity</div>
          <div className="mono text-xs text-white mt-0.5">${fmtUsd(r.balance)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#6B7280]">Trades</div>
          <div className="mono text-xs text-white mt-0.5">{r.trades || 0}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#6B7280]">Win %</div>
          <div className="mono text-xs mt-0.5" style={{ color: r.win_rate >= 50 ? "#0ECB81" : "#9CA3AF" }}>
            {r.trades > 0 ? `${(r.win_rate || 0).toFixed(0)}%` : "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LeaderboardPage() {
  const [data, setData] = useState({ top: [], me: null, total_traders: 0, period: "all" });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");

  const load = async (p = period) => {
    setLoading(true);
    try {
      const r = await api.get(`/leaderboard?period=${p}`);
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(period);
    const i = setInterval(() => load(period), 30000);
    return () => clearInterval(i);
  }, [period]);

  return (
    <div className="min-h-screen bg-[#0A0E17] text-white pb-24" data-testid="leaderboard-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* Header */}
        <div className="flex items-end justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#FCD535] flex items-center justify-center rounded-sm">
              <Ranking size={20} weight="fill" className="text-black" />
            </div>
            <div>
              <p className="overline text-[#FCD535]">Leaderboard</p>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight leading-tight">
                Top Traders
              </h1>
            </div>
          </div>
          <button
            onClick={() => load(period)}
            data-testid="lb-refresh"
            className="text-[#9CA3AF] hover:text-white p-2 -mr-2"
            title="Refresh"
          >
            <ArrowsClockwise size={18} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        {/* Period pills */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex gap-1 bg-[#0D1117] border border-[#1F2937] rounded-sm p-1">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                data-testid={`lb-period-${p.id}`}
                className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-sm transition-colors ${
                  period === p.id
                    ? "bg-[#FCD535] text-black"
                    : "text-[#9CA3AF] hover:text-white"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table (desktop) */}
        <div className="hidden md:block bg-[#0D1117] border border-[#1F2937]">
          {/* Header */}
          <div className="grid grid-cols-[1fr_110px_130px_80px_80px] gap-3 px-5 py-2.5 border-b border-[#1F2937] bg-[#0B0F19]">
            <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-semibold">Trader</div>
            <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-semibold text-right">ROI %</div>
            <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-semibold text-right">Realized PnL</div>
            <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-semibold text-right">Trades</div>
            <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-semibold text-right">Win %</div>
          </div>
          {/* Body */}
          {loading && data.top.length === 0 ? (
            <div className="text-center py-12 text-sm text-[#9CA3AF]">Loading…</div>
          ) : data.top.length === 0 ? (
            <div className="text-center py-12 text-sm text-[#9CA3AF]">No trades yet for this period</div>
          ) : (
            <div>
              {data.top.map((r) => (
                <Row key={`${r.rank}-${r.ident}`} r={r} isMe={data.me && data.me.rank === r.rank && data.me.ident === r.ident} />
              ))}
            </div>
          )}
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-1.5">
          {loading && data.top.length === 0 ? (
            <div className="text-center py-12 text-sm text-[#9CA3AF]">Loading…</div>
          ) : data.top.length === 0 ? (
            <div className="text-center py-12 text-sm text-[#9CA3AF]">No trades yet for this period</div>
          ) : (
            data.top.map((r) => (
              <MobileCard key={`${r.rank}-${r.ident}`} r={r} isMe={data.me && data.me.rank === r.rank && data.me.ident === r.ident} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
